import type { ApiClient } from '@treneo/infrastructure/api';
import { BaseApi } from '@treneo/infrastructure/api';
import type {
  IConnectToFreshStartDesktopRequest,
  IConnectToMyDesktopRequest,
  IConnectToParticipantDesktopRequest,
  IConnectToTrainerDesktopRequest,
  ICreateFreshStartFromOsAsyncTemplateRequest,
  ICreateFreshStartFromStandaloneAsyncTemplateRequest,
  IDeleteDesktopAsyncRequest,
  IDeleteFreshStartDesktopRequest,
  IDesktopModel,
  IGetCourseDesktopsRequest,
  IGetCourseDesktopsResponse,
  IGetMyDesktopsForCoursesRequest,
  IGetMyDesktopsForCoursesResponse,
  IGetParticipantsRequest,
  IGetParticipantsResponse,
  IGetStandalonesResponse,
  IGetTrainerDesktopRequest,
  IGetTrainerDesktopsForCoursesRequest,
  IGetTrainerDesktopsForCoursesResponse,
  IStartParticipantDesktopRequest,
  IStopDesktopRequest,
  Result
} from '@treneo/models';

export class DesktopsApi extends BaseApi {
  protected readonly baseUrl = '/api/courses';

  constructor(apiClient: ApiClient) {
    super(apiClient);
  }

  connectToMyDesktop = (
    request: IConnectToMyDesktopRequest
  ): Promise<Result<IDesktopModel | null>> =>
    this.post<IDesktopModel | null>('/connectToMyDesktop', request, 'Connect to my desktop failed');

  connectToTrainerDesktop = (
    request: IConnectToTrainerDesktopRequest
  ): Promise<Result<IDesktopModel | null>> =>
    this.post<IDesktopModel | null>(
      '/connectToTrainerDesktop',
      request,
      'Connect to trainer desktop failed'
    );

  connectToFreshStartDesktop = (
    request: IConnectToFreshStartDesktopRequest
  ): Promise<Result<IDesktopModel | null>> =>
    this.post<IDesktopModel | null>(
      '/connectToFreshStartDesktop',
      request,
      'Connect to fresh start desktop failed'
    );

  connectToParticipantDesktop = (
    request: IConnectToParticipantDesktopRequest
  ): Promise<Result<IDesktopModel | null>> =>
    this.post<IDesktopModel | null>(
      '/connectToParticipantDesktop',
      request,
      'Connect to participant desktop failed'
    );

  getMyDesktop = (courseId: string, signal?: AbortSignal): Promise<Result<IDesktopModel | null>> =>
    this.get<IDesktopModel | null>('/myDesktop', { courseId }, 'Get my desktop failed', signal);

  getTrainerDesktop = (
    request: IGetTrainerDesktopRequest,
    signal?: AbortSignal
  ): Promise<Result<IDesktopModel | null>> =>
    this.get<IDesktopModel | null>(
      '/trainerDesktop',
      request,
      'Get trainer desktop failed',
      signal
    );

  getFreshStartDesktop = (
    courseId: string,
    signal?: AbortSignal
  ): Promise<Result<IDesktopModel | null>> =>
    this.get<IDesktopModel | null>(
      '/freshStartDesktop',
      { courseId },
      'Get fresh start desktop failed',
      signal
    );

  getCourseDesktops = (
    request: IGetCourseDesktopsRequest,
    signal?: AbortSignal
  ): Promise<Result<IGetCourseDesktopsResponse>> =>
    this.get<IGetCourseDesktopsResponse>(
      '/desktops',
      request,
      'Get course desktops failed',
      signal
    );

  getMyDesktopsForCourses = (
    request: IGetMyDesktopsForCoursesRequest
  ): Promise<Result<IGetMyDesktopsForCoursesResponse>> =>
    this.post<IGetMyDesktopsForCoursesResponse>(
      '/myDesktopsForCourses',
      request,
      'Get my desktops for courses failed'
    );

  getTrainerDesktopsForCourses = (
    request: IGetTrainerDesktopsForCoursesRequest
  ): Promise<Result<IGetTrainerDesktopsForCoursesResponse>> =>
    this.post<IGetTrainerDesktopsForCoursesResponse>(
      '/trainerDesktopsForCourses',
      request,
      'Get trainer desktops for courses failed'
    );

  startParticipantDesktop = (
    request: IStartParticipantDesktopRequest
  ): Promise<Result<IDesktopModel | null>> =>
    this.post<IDesktopModel | null>(
      '/startParticipantDesktop',
      request,
      'Start participant desktop failed'
    );

  getParticipants = (request: IGetParticipantsRequest): Promise<Result<IGetParticipantsResponse>> =>
    this.get<IGetParticipantsResponse>('/participants', request, 'Get participants failed');

  getStandalones = (): Promise<Result<IGetStandalonesResponse>> =>
    this.get<IGetStandalonesResponse>('/standalones', null, 'Get standalones failed');

  stopDesktop = (request: IStopDesktopRequest): Promise<void> =>
    this.postVoid('/stopDesktop', request);

  deleteDesktop = (request: IDeleteDesktopAsyncRequest): Promise<void> =>
    this.postVoid('/deleteDesktop', request);

  deleteFreshStartDesktop = (request: IDeleteFreshStartDesktopRequest): Promise<void> =>
    this.postVoid('/deleteFreshStartDesktop', request);

  createFreshStartFromStandaloneTemplate = (
    request: ICreateFreshStartFromStandaloneAsyncTemplateRequest
  ): Promise<void> => this.postVoid('/createFreshStartFromStandaloneTemplate', request);

  createFreshStartFromOsTemplate = (
    request: ICreateFreshStartFromOsAsyncTemplateRequest
  ): Promise<void> => this.postVoid('/createFreshStartFromOsTemplate', request);
}
